<i18n>
{
  "en": {
    "typeToSearch": "Type to search...",
    "tapToSearch": "Tap to search..."
  },
  "es": {
    "typeToSearch": "Escribe para buscar...",
    "tapToSearch": "Pulsa para buscar..."
  }
}
</i18n>
<template>
  <fragment>
    <div tabindex="0" ref="modal" class="pt-2 pb-1 px-3 ma-0 modal"
         v-show="showModal">
      <v-btn icon color="red" outlined class="align-self-end" @click="onToggleModal">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-text-field autofocus @input="onFilterListDebounce" :placeholder="$t('typeToSearch')"/>
      <v-virtual-scroll :items="itemsToDisplay" item-height="62px" bench="12">
        <template v-slot:default="data">
          <v-list-item @click="onItemClick(data.item)">
            <slot name="item" v-bind="data"/>
          </v-list-item>
          <v-divider/>
        </template>
      </v-virtual-scroll>
    </div>
    <v-autocomplete v-if="isMobile && onlyItemSlot" v-bind="$attrs" v-on="$listeners"
                    v-model="dataValue" @input="vModelHandler" :items="items" :item-text="itemText"
                    :item-value="itemValue" :rules="rules" :filter="filter" :placeholder="$t('tapToSearch')"
                    :return-object="returnObject" :persistent-hint="persistentHint" :required="required"
                    :disabled="disabled" :label="label" prepend-icon="mdi-magnify" append-icon=""
                    :multiple="multiple" :class="{...cssClass, required: required}" readonly chips
                    deletable-chips @click.stop.prevent="onToggleModal">
      <template v-slot:item="data">
        <slot name="item" v-bind="data"/>
      </template>
    </v-autocomplete>

    <v-autocomplete v-else-if="isMobile && itemAndSelectionSlot" v-bind="$attrs" v-on="$listeners"
                    v-model="dataValue" @input="vModelHandler" :items="items" :item-text="itemText"
                    :item-value="itemValue" :rules="rules" :filter="filter" :placeholder="$t('tapToSearch')"
                    :return-object="returnObject" :chips="chips" :persistent-hint="persistentHint"
                    :required="required" :disabled="disabled" :label="label" prepend-icon="mdi-magnify"
                    append-icon="" :multiple="multiple" :class="{...cssClass, required: required}"
                    readonly @click.stop.prevent="onToggleModal">
      <template v-slot:item="data">
        <slot name="item" v-bind="data"/>
      </template>
      <template v-slot:selection="data">
        <slot name="selection" v-bind="data"/>
      </template>
    </v-autocomplete>

    <!--    Desktop Autocompletes-->
    <v-autocomplete v-else-if="isNotMobile && onlyItemSlot" v-bind="$attrs" v-on="$listeners"
                    v-model="dataValue" @input="vModelHandler" :items="items" :item-text="itemText"
                    :item-value="itemValue" :rules="rules" :filter="filter" :placeholder="placeholder"
                    :return-object="returnObject" :chips="chips" :persistent-hint="persistentHint"
                    :required="required" :disabled="disabled" :label="label" :prepend-icon="prependIcon"
                    :multiple="multiple" :class="{...cssClass, required: required}">
      <template v-slot:item="data">
        <slot name="item" v-bind="data"/>
      </template>
    </v-autocomplete>

    <v-autocomplete v-else-if="isNotMobile && itemAndSelectionSlot" v-bind="$attrs" v-on="$listeners"
                    v-model="dataValue" @input="vModelHandler" :items="items" :item-text="itemText"
                    :item-value="itemValue" :rules="rules" :filter="filter" :placeholder="placeholder"
                    :return-object="returnObject" :chips="chips" :persistent-hint="persistentHint"
                    :required="required" :disabled="disabled" :label="label" :prepend-icon="prependIcon"
                    :multiple="multiple" :class="{...cssClass, required: required}">
      <template v-slot:item="data">
        <slot name="item" v-bind="data"/>
      </template>
      <template v-slot:selection="data">
        <slot name="selection" v-bind="data"/>
      </template>
    </v-autocomplete>
  </fragment>

</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import debounce from 'lodash/debounce';

@Component
export default class DynameatAutocomplete extends Vue {
  // Functions to extract data from
  @Prop({ type: Array, required: true }) items;

  @Prop({ type: String, default: undefined }) itemValue;

  @Prop({ type: String, default: undefined }) itemText;

  // Function type props
  @Prop({ type: Array, default: undefined }) rules;

  @Prop({ type: Function, default: undefined }) filter;

  // Boolean props
  @Prop({ type: Boolean, default: false }) returnObject;

  @Prop({ type: Boolean, default: false }) chips;

  @Prop({ type: Boolean, default: false }) persistentHint;

  @Prop({ type: Boolean, default: false }) required;

  @Prop({ type: Boolean, default: false }) disabled;

  // Strings for autocomplete
  @Prop({ type: String, default: undefined }) label

  @Prop({ type: String, default: undefined }) prependIcon;

  @Prop({ type: Boolean, default: false }) multiple;

  @Prop({ type: String, default: undefined }) cssClass;

  @Prop({ type: String, default: undefined }) placeholder;

  @Prop({ default: null }) value;

  showModal = false;

  dataValue = null

  itemsToDisplay = []

  get isMobile() {
    return this.$vuetify.breakpoint.smAndDown;
  }

  get isNotMobile() {
    return !this.isMobile;
  }

  get noSlot() {
    return !this.hasSlot('item') && !this.hasSlot('selection');
  }

  get onlyItemSlot() {
    return !!this.hasSlot('item') && !this.hasSlot('selection');
  }

  get onlySelectionSlot() {
    return !this.hasSlot('item') && !!this.hasSlot('selection');
  }

  get itemAndSelectionSlot() {
    return !!this.hasSlot('item') && !!this.hasSlot('selection');
  }

  created() {
    this.dataValue = this.value;
    this.itemsToDisplay = this.items;
  }

  mounted() {
    if (this.isMobile && (this.noSlot || this.onlySelectionSlot)) throw new Error('At least "item" slot is required for mobile mode');
  }

  vModelHandler(newValue) {
    if (newValue) {
      if (this.isMobile && this.multiple) {
        this.dataValue.push(newValue);
        const returnValues = this.returnObject ? this.dataValue : this.dataValue.map((el) => this.getItemValue(el));
        this.$emit('input', returnValues);
        this.$emit('change', returnValues);
      } else {
        this.dataValue = newValue;
        const returnValue = this.returnObject ? newValue : this.getItemValue(newValue);
        this.$emit('input', returnValue);
        this.$emit('change', returnValue);
      }
    }
  }

  hasSlot(name) {
    return !!this.$slots[name] || !!this.$scopedSlots[name];
  }

  onOpenModal() {
    this.showModal = true;
  }

  onToggleModal() {
    this.showModal = !this.showModal;
  }

  getItemText(item) {
    return this.itemText ? item[this.itemText] : item.text;
  }

  getItemValue(item) {
    if (item) {
      return this.itemValue ? item[this.itemValue] : item.value;
    }
    return item;
  }

  onFilterListDebounce = debounce(this.onFilterList, 400)

  onFilterList(itemFilterText) {
    if (itemFilterText.trim() === '') {
      this.itemsToDisplay = this.items;
    } else {
      this.itemsToDisplay = this.items.filter((item) => this.filter(item, itemFilterText));
    }
  }

  onItemClick(item) {
    this.vModelHandler(item);
    this.onToggleModal();
  }

  hideVuetifyModals() {
    const documents = document.querySelectorAll('[role="document"]');
    documents.forEach((el) => el.style.display = 'none');
  }

  showVuetifyModals() {
    const documents = document.querySelectorAll('[role="document"]');
    documents.forEach((el) => el.style.removeProperty('display'));
  }

  @Watch('value', { immediate: true })
  vmModelUpdate(newValue) {
    this.dataValue = newValue;
  }

  @Watch('showModal')
  onShowModalChange(showModal) {
    if (showModal) {
      document.getElementById('app').appendChild(this.$refs.modal);
      this.hideVuetifyModals();
    } else {
      document.getElementById('app').removeChild(this.$refs.modal);
      this.showVuetifyModals();
    }
  }
}
</script>

<style scoped lang="scss">
.modal {
  background-color: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
  pointer-events: auto;
  position: fixed;
  width: 100%;
  z-index: 999;
}
</style>

<style>
/*Used to correct a bug when showing v-autocomplete placeholder for current vuetify version: https://github.com/vuetifyjs/vuetify/issues/11553*/
.v-autocomplete:not(.v-input--is-focused).v-select--chips input {
  max-height: inherit !important;
}

.v-input__prepend-outer {
  margin-top: 14px;
}

.required label::after {
  content: " *";
  color: red;
}
</style>
